/* eslint-disable max-statements */
import { getMaxApplePayVersion } from './getMaxApplePayVersion';
import { reduceNetworksToSupported } from './reduceNetworksToSupported';

export interface ApplePaySessionConfig {
    request: ApplePayJS.ApplePayPaymentRequest;
    onValidateMerchant: (url: string) => Promise<unknown>;
    onAuthorizePayment: (token: ApplePayJS.ApplePayPaymentToken) => Promise<void>;
    onCancel?: () => void;
    onDone?: () => void;
}

export const startApplePaySession = ({
    request,
    onCancel,
    onDone,
    onValidateMerchant,
    onAuthorizePayment,
}: ApplePaySessionConfig) => {
    if (!ApplePaySession.canMakePayments()) {
        throw new Error('Apple Pay is not available');
    }

    const version = getMaxApplePayVersion();
    const requestSafe = reduceNetworksToSupported(version, request);

    const session = new ApplePaySession(version, requestSafe);

    if (onCancel) {
        session.oncancel = onCancel;
    }

    session.onvalidatemerchant = async (event) => {
        try {
            const merchantSession = await onValidateMerchant(event.validationURL);

            session.completeMerchantValidation(merchantSession);
        } catch (e: unknown) {
            console.error('[Apple Pay] Error validating merchant', e);
            session.abort();
            if (onCancel) {
                onCancel();
            }
        }
    };

    session.onpaymentauthorized = async (event) => {
        try {
            let status = ApplePaySession.STATUS_SUCCESS;

            try {
                await onAuthorizePayment(event.payment.token);
            } catch (e: unknown) {
                console.error('[Apple Pay] Payment failed', e);
                status = ApplePaySession.STATUS_FAILURE;
            }

            session.completePayment(status);

            if (onDone) {
                onDone();
            }
        } catch (e: unknown) {
            console.error('[Apple Pay] Error authorizing payment', e);
            session.abort();
            if (onCancel) {
                onCancel();
            }
        }
    };

    session.begin();

    return session;
};
